import {
  Button,
  column,
  dateColumn,
  GridColumn,
  GridDataRow,
  GridTable,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
  useModal,
} from "@homebound/beam";
import { Percentage, Price } from "src/components";
import {
  CreditFacilityInterestType,
  FundLoanManagementPageDrawFragment,
  FundLoanManagementPageFundFragment,
} from "src/generated/graphql-types";
import { createDrawDetailsUrl } from "src/RouteUrls";
import { formatWithYear } from "src/utils/dates";
import { CreateDrawModal } from "../createDrawModal/CreateDrawModal";
import { BillExportModal } from "../billExportModal/BillExportModal";

export type FundDrawsTableProps = {
  draws: FundLoanManagementPageDrawFragment[];
  fund: FundLoanManagementPageFundFragment;
};

export function FundDrawsTable(props: FundDrawsTableProps) {
  const { draws, fund } = props;

  const { openModal } = useModal();
  const displayRate = fund.creditFacilityInterestType.code === CreditFacilityInterestType.FixedAtDraw;
  return (
    <>
      <GridTable
        id="drawsTable"
        rowStyles={rowStyles}
        columns={createDrawColumns(displayRate)}
        rows={createDrawRows(draws)}
        fallbackMessage="Draws for this Fund will show here."
      />
      <Button
        label="Create Draw"
        variant="secondary"
        onClick={() =>
          openModal({
            content: <CreateDrawModal fundId={fund.id} displayRate={displayRate} drawRequestId={undefined} />,
          })
        }
      />
      <Button
        label="Create Bill Export For Draw"
        variant="secondary"
        onClick={() =>
          openModal({
            content: <BillExportModal fundId={fund.id} endDate={undefined} minimumBillAmountInCents={undefined} />,
          })
        }
      />
    </>
  );
}

type Row = SimpleHeaderAndData<FundLoanManagementPageDrawFragment>;

const rowStyles: RowStyles<Row> = {
  header: {},
  data: {
    onClick: ({ data }) => {
      window.open(`${createDrawDetailsUrl(data.id)}`, "_blank", "noopener,noreferrer");
    },
  },
};

function createDrawRows(draws: FundLoanManagementPageDrawFragment[]): GridDataRow<Row>[] {
  return simpleDataRows(draws);
}

function createDrawColumns(displayRate: boolean): GridColumn<Row>[] {
  const idColumn = column<Row>({
    header: () => "Credit Facility Draw ID",
    data: ({ id }) => id,
  });

  const drawDateColumn = dateColumn<Row>({
    header: () => "Draw Date",
    data: ({ drawDate }) => formatWithYear(drawDate),
  });

  const amountColumn = column<Row>({
    header: () => "Amount Drawn",
    data: ({ amountInCents }) => getPriceComponent(amountInCents),
  });

  const columns = [idColumn, drawDateColumn, amountColumn];
  if (displayRate) {
    const rateColumn = column<Row>({
      header: () => "Interest Rate",
      data: ({ fixedRateThousandthsBasisPoints }) =>
        getPercentFromMaybeThousandthsComponent(fixedRateThousandthsBasisPoints),
    });
    columns.push(rateColumn);
  }
  return columns;
}

function getPriceComponent(value: number | undefined) {
  return <Price valueInCents={value} dropZero />;
}

function getPercentFromMaybeThousandthsComponent(value: number | undefined | null) {
  const percent = value ? value / 1000_00 : 0;
  return <Percentage percent={percent} dropZero />;
}
